/**
 * Öffentliche Abendessen-Anmeldung — Route: /seminar-dinner/:seminarId
 * Weiche Frist: Banner-Hinweis, Formular bleibt dauerhaft offen (keine Hard-Sperren).
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { AlertTriangle, Check, Loader2, Minus, Plus, UtensilsCrossed } from 'lucide-react';
import { db } from '../../config/firebase';
import { PE_LOGO_SRC } from '../../constants/brandAssets';
import {
  SEMINAR_DINNER_CONFIGS_COLLECTION,
  type SeminarDinnerConfig,
  type SeminarDinnerDay,
} from '../../types/seminarDinner';
import { useSeminarDinnerStore } from '../../store/seminarDinnerStore';

const GOLD = '#dcb68a';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** 0 = nicht dabei; sonst exakte Personenanzahl */
const HEADCOUNT_MIN = 0;
const HEADCOUNT_MAX = 50;

type DaySelection = Record<string, number>;

const inputClass =
  'w-full rounded-xl bg-slate-950 border border-slate-800 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#dcb68a] focus:ring-1 focus:ring-[#dcb68a]/30 transition-colors placeholder:text-slate-600';

function formatDayLabel(day: SeminarDinnerDay): string {
  const iso = String(day.datum || '').trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(iso)) {
    const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
    if (!isNaN(d.getTime())) {
      return `${day.label} · ${d.toLocaleDateString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })}`;
    }
  }
  return day.datum ? `${day.label} · ${day.datum}` : day.label;
}

const SeminarDinnerRegistrationPage: React.FC = () => {
  const { seminarId: rawId } = useParams<{ seminarId: string }>();
  const seminarId = (rawId || '').trim();

  const addRegistration = useSeminarDinnerStore((s) => s.addRegistration);

  const [config, setConfig] = useState<SeminarDinnerConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [bemerkung, setBemerkung] = useState('');
  const [selection, setSelection] = useState<DaySelection>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<{
    name: string;
    days: Array<{ label: string; count: number }>;
  } | null>(null);

  useEffect(() => {
    if (!seminarId) {
      setLoadError('Kein Seminar angegeben.');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setLoadError(null);
    const unsub = onSnapshot(
      doc(db, SEMINAR_DINNER_CONFIGS_COLLECTION, seminarId),
      (snap) => {
        if (!snap.exists()) {
          setConfig(null);
          setLoadError('Für dieses Seminar ist noch keine Abendessen-Anmeldung freigeschaltet.');
          setIsLoading(false);
          return;
        }
        const data = snap.data() as Record<string, unknown>;
        const tageRaw = Array.isArray(data.tage) ? data.tage : [];
        const tage: SeminarDinnerDay[] = tageRaw
          .map((item: any, index: number) => ({
            tagId: String(item?.tagId || item?.id || `tag_${index}`),
            label: String(item?.label || `Abend ${index + 1}`),
            datum: String(item?.datum || item?.date || ''),
            restaurantName: item?.restaurantName != null ? String(item.restaurantName) : undefined,
            adresseTreffpunkt:
              item?.adresseTreffpunkt != null ? String(item.adresseTreffpunkt) : undefined,
            uhrzeit: item?.uhrzeit != null ? String(item.uhrzeit) : undefined,
          }))
          .filter((t) => String(t.datum || '').trim().length > 0);
        const next: SeminarDinnerConfig = {
          seminarId,
          titel: String(data.titel || data.title || 'Seminar'),
          tage,
        };
        setConfig(next);
        setSelection((prev) => {
          const mapped: DaySelection = {};
          tage.forEach((t) => {
            const existing = prev[t.tagId];
            mapped[t.tagId] = typeof existing === 'number' ? existing : 0;
          });
          return mapped;
        });
        setLoadError(null);
        setIsLoading(false);
      },
      (err) => {
        console.error('[SeminarDinnerRegistrationPage]', err);
        setLoadError('Abendessen-Daten konnten nicht geladen werden.');
        setIsLoading(false);
      }
    );
    return () => unsub();
  }, [seminarId]);

  const adjustCount = (tagId: string, delta: number) => {
    setSelection((prev) => {
      const cur = typeof prev[tagId] === 'number' ? prev[tagId] : 0;
      const next = Math.min(HEADCOUNT_MAX, Math.max(HEADCOUNT_MIN, cur + delta));
      return { ...prev, [tagId]: next };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!config || isSubmitting) return;
    setSubmitError(null);

    const cleanName = name.trim();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanName || cleanName.length < 2) {
      setSubmitError('Bitte deinen Namen angeben.');
      return;
    }
    if (!EMAIL_REGEX.test(cleanEmail)) {
      setSubmitError('Bitte eine gültige E-Mail-Adresse angeben.');
      return;
    }

    const anmeldungen: Record<string, number> = {};
    const confirmedDays: Array<{ label: string; count: number }> = [];
    (config.tage || []).forEach((t) => {
      const count = Math.floor(Number(selection[t.tagId]) || 0);
      if (count < 1) return;
      if (count > HEADCOUNT_MAX) return;
      anmeldungen[t.tagId] = count;
      confirmedDays.push({ label: formatDayLabel(t), count });
    });

    if (!confirmedDays.length) {
      setSubmitError('Bitte mindestens einen Abend mit Personenanzahl wählen (+).');
      return;
    }

    setIsSubmitting(true);
    try {
      await addRegistration({
        seminarId: config.seminarId,
        name: cleanName,
        email: cleanEmail,
        anmeldungen,
        bemerkung: bemerkung.trim() || undefined,
        isManualEntry: false,
      });
      setConfirmed({ name: cleanName, days: confirmedDays });
      setName('');
      setEmail('');
      setBemerkung('');
      setSelection((prev) => {
        const next: DaySelection = {};
        Object.keys(prev).forEach((k) => {
          next[k] = 0;
        });
        return next;
      });
    } catch (err) {
      console.error('[SeminarDinnerRegistrationPage] submit:', err);
      setSubmitError('Anmeldung fehlgeschlagen. Bitte später erneut versuchen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top,_rgba(220,182,138,0.08),_transparent_55%)]" />
      <div className="relative max-w-xl mx-auto px-4 py-10 md:py-16">
        <div className="flex justify-center mb-8">
          <img
            src={PE_LOGO_SRC}
            alt="PreisExpert"
            className="h-36 md:h-44 w-auto max-w-full object-contain"
          />
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl">
          <div className="px-6 py-5 border-b border-slate-800 flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center border border-slate-700"
              style={{ background: 'rgba(220,182,138,0.12)', color: GOLD }}
            >
              <UtensilsCrossed className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: GOLD }}>
                Abendessen-Anmeldung
              </p>
              <h1 className="text-xl font-bold text-white leading-tight">
                {config?.titel || (isLoading ? 'Lädt…' : 'Seminar')}
              </h1>
            </div>
          </div>

          {/* Soft-Deadline: Banner dauerhaft; Formular nie gesperrt */}
          {!confirmed && (
            <div className="mx-4 mt-4 mb-0 rounded-xl border border-amber-500/40 bg-amber-950/40 px-4 py-3 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-amber-100 text-sm leading-relaxed">
                <strong className="font-bold">WICHTIGER HINWEIS:</strong> Bitte trage deine Teilnahme
                idealerweise bis spätestens 7 Tage vor Seminarbeginn ein, damit wir ausreichend Plätze
                im Restaurant reservieren können. Spätere Anmeldungen versuchen wir nach Verfügbarkeit
                zu berücksichtigen. Die Abendessen sind Selbstzahler (Zahlung vor Ort).
              </p>
            </div>
          )}

          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin" /> Laden…
              </div>
            ) : loadError && !config ? (
              <p className="text-center text-slate-400 py-12 text-sm">{loadError}</p>
            ) : confirmed ? (
              <div className="text-center py-6 space-y-4">
                <div
                  className="mx-auto w-14 h-14 rounded-full flex items-center justify-center border"
                  style={{ background: 'rgba(220,182,138,0.15)', borderColor: GOLD, color: GOLD }}
                >
                  <Check className="w-7 h-7" />
                </div>
                <h2 className="text-2xl font-bold text-white">Anmeldung gespeichert</h2>
                <p className="text-slate-400 text-sm">
                  Danke, {confirmed.name}. Wir haben dich für folgende Abende vorgemerkt:
                </p>
                <ul className="text-left max-w-sm mx-auto space-y-2">
                  {confirmed.days.map((d) => (
                    <li
                      key={d.label}
                      className="bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm flex justify-between gap-3"
                    >
                      <span className="text-white">{d.label}</span>
                      <span style={{ color: GOLD }} className="font-bold shrink-0">
                        {d.count} Pers.
                      </span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => setConfirmed(null)}
                  className="mt-4 text-xs uppercase tracking-widest font-bold text-slate-500 hover:text-white transition-colors"
                >
                  Weitere Anmeldung
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <p className="text-slate-400 text-sm leading-relaxed">
                  Sag uns nur, an welchen Abenden du dabei bist. Restaurant, Treffpunkt und
                  Uhrzeit folgen in einer zweiten Mail, sobald alles feststeht.
                </p>
                <p className="text-sm rounded-xl border border-[#dcb68a]/30 bg-[#dcb68a]/10 px-4 py-3 text-[#dcb68a]">
                  <strong className="font-bold text-white">Selbstzahler:</strong> Die Abendessen
                  zahlt jeder vor Ort im Restaurant selbst — keine Seminar-Zusatzkosten über uns.
                </p>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
                    Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={inputClass}
                    placeholder="Vor- und Nachname"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
                    E-Mail
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    placeholder="name@beispiel.de"
                    autoComplete="email"
                  />
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                    Abende & Personenanzahl
                  </p>
                  <p className="text-slate-500 text-xs mb-3">
                    Mit + Personen setzen. 0 = an dem Abend nicht dabei.
                  </p>
                  <div className="space-y-3">
                    {(config?.tage || []).map((day) => {
                      const count = typeof selection[day.tagId] === 'number' ? selection[day.tagId] : 0;
                      const active = count > 0;
                      return (
                        <div
                          key={day.tagId}
                          className={`rounded-xl border p-4 flex items-center justify-between gap-4 transition-colors ${
                            active
                              ? 'border-[#dcb68a]/50 bg-slate-950'
                              : 'border-slate-800 bg-slate-950/50'
                          }`}
                        >
                          <div className="min-w-0">
                            <p className="text-white font-medium text-sm truncate">
                              {formatDayLabel(day)}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {active ? `${count} Person${count === 1 ? '' : 'en'}` : 'nicht dabei'}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => adjustCount(day.tagId, -1)}
                              disabled={count <= HEADCOUNT_MIN}
                              aria-label="Weniger Personen"
                              className="w-11 h-11 rounded-xl border border-slate-700 bg-slate-900 text-white flex items-center justify-center hover:border-[#dcb68a] hover:text-[#dcb68a] transition-colors disabled:opacity-30 disabled:hover:border-slate-700 disabled:hover:text-white"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span
                              className="w-12 text-center text-lg font-black tabular-nums"
                              style={{ color: active ? GOLD : '#64748b' }}
                            >
                              {count}
                            </span>
                            <button
                              type="button"
                              onClick={() => adjustCount(day.tagId, 1)}
                              disabled={count >= HEADCOUNT_MAX}
                              aria-label="Mehr Personen"
                              className="w-11 h-11 rounded-xl border border-slate-700 bg-slate-900 text-white flex items-center justify-center hover:border-[#dcb68a] hover:text-[#dcb68a] transition-colors disabled:opacity-30 disabled:hover:border-slate-700 disabled:hover:text-white"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {!(config?.tage || []).length && (
                      <p className="text-slate-500 text-sm italic">Noch keine Abende hinterlegt.</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
                    Bemerkung (optional)
                  </label>
                  <textarea
                    value={bemerkung}
                    onChange={(e) => setBemerkung(e.target.value)}
                    className={`${inputClass} min-h-[4.5rem] resize-y`}
                    placeholder="Allergien, besondere Wünsche…"
                  />
                </div>

                {submitError && (
                  <p className="text-red-400 text-sm border border-red-500/30 bg-red-950/30 rounded-xl px-4 py-3">
                    {submitError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 rounded-xl font-black uppercase tracking-widest text-sm transition-colors disabled:opacity-50"
                  style={{ background: GOLD, color: '#0f172a' }}
                >
                  {isSubmitting ? (
                    <span className="inline-flex items-center gap-2 justify-center">
                      <Loader2 className="w-4 h-4 animate-spin" /> Speichere…
                    </span>
                  ) : (
                    'Anmeldung absenden'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeminarDinnerRegistrationPage;
